import { leerToken } from '../utils/sesion';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { STATUS_CONFIG, QUALIFICATION_CONFIG, SOURCE_CONFIG, formatDateShort, getSourceLabel, estadoInfo, estadosPara, LINEAS, DECISOR, esProspecto, TIPOS_MAIL, RESPUESTAS_MAIL } from '../utils/constants';
import ManualLeadModal from './ManualLeadModal';
import { Star, Trash2, MessageCircle, Search, Download, Plus, Filter, Globe, MapPin, Mail, ExternalLink } from 'lucide-react';
import api from '../utils/api';
import { leerCampos, valorDe, mostrar } from './CamposPropios';
import LeadDetail from './LeadDetail';
import WhatsAppModal from './WhatsAppModal';
import { hasFeature } from '../utils/features';

export default function LeadTable({ leads, onRefresh, showDatasetCol, total, page, pages, filters, setFilters, datasets }) {
  const { tenant, user } = useAuth();
  const canWhatsApp = hasFeature(tenant, user, 'lead_whatsapp');
  const [selectedLead, setSelectedLead] = useState(null);
  const [waLead, setWaLead] = useState(null);
  /* Las columnas propias salen de la MISMA configuración que los campos de la
     ficha (`lead_fields`), las que tienen `lista: true`. Antes esto leía de
     otro lado (`features.custom_columns`), así que había que definir los campos
     dos veces y se desincronizaban. Se sigue aceptando el formato viejo por si
     algún cliente lo tenía cargado. */
  const [camposPropios, setCamposPropios] = useState([]);
  // la casilla de la prospección por mail: el link lo carga el agente de Prospección
  const [webmail, setWebmail] = useState('');
  useEffect(() => { api.get('/settings').then(r => { setCamposPropios(leerCampos(r.data)); setWebmail(r.data?.webmail_prospeccion_mail || ''); }).catch(() => {}); }, []);
  const customCols = camposPropios.filter(c => c.lista).length
    ? camposPropios.filter(c => c.lista)
    : (tenant?.features?.custom_columns || []);
  /* Lo propio de los prospectos (columna Observación, botón de web, filtro por
     día de carga) solo aparece cuando en la lista hay prospectos: el resto de
     los CRM no ve nada distinto. */
  const hayProspectos = (leads || []).some(l => esProspecto(l.source));
  const hayMail = (leads || []).some(l => l.source === 'prospeccion_mail') || filters.source === 'prospeccion_mail';
  // En el CRM de la agencia el teléfono va como tilde: el número está en el panel y se contacta con el botón
  const esAgencia = /^agencia\./.test(window.location.hostname);
  // El color del estado, mucho más suave, de fondo de la fila: se ve de un vistazo en qué anda cada lead
  const tenue = (bg) => String(bg || '').replace(/rgba\(([^)]*),\s*[\d.]+\)/, 'rgba($1, 0.14)') || 'transparent';
  const hoyAr = () => new Date(Date.now() - 3 * 3600 * 1000).toISOString().slice(0, 10);
  const diaAntes = (iso) => new Date(Date.parse(iso + 'T12:00:00Z') - 86400000).toISOString().slice(0, 10);
  const [showManual, setShowManual] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ categories: [], cities: [], sources: [], utmCampaigns: [] });
  const datasetId = filters?.dataset_id;

  useEffect(() => {
    api.get('/leads/meta/filters', { params: { dataset_id: datasetId } }).then(res => setFilterOptions(res.data)).catch(() => {});
  }, [datasetId]);

  const exportCSV = () => {
    const token = leerToken();
    const params = new URLSearchParams();
    Object.entries(filters || {}).forEach(([k, v]) => { if (v && k !== 'page' && k !== 'search') params.set(k, v); });
    fetch(`/api/leads/export/csv?${params.toString()}`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.blob()).then(blob => {
        const a = document.createElement('a'); a.href = window.URL.createObjectURL(blob);
        a.download = `leads_${Date.now()}.csv`; document.body.appendChild(a); a.click(); a.remove();
        toast.success('CSV descargado');
      }).catch(() => toast.error('Error al exportar'));
  };
  const extraFilterKeys = ['status', 'qualification', 'city', 'category', 'source', 'utm_campaign'];
  const activeFilterCount = filters ? extraFilterKeys.filter(k => filters[k]).length : 0;

  const getExtraField = (lead, campo) => mostrar(campo, valorDe(lead, campo.key));

  const openLead = (lead) => {
    setSelectedLead(lead);
  };

  return (
    <>
      {filters && setFilters && (<>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 12, alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: '1 1 200px', maxWidth: 360 }}>
            <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input placeholder="Buscar leads..." value={filters.search || ''} onChange={e => setFilters({ ...filters, search: e.target.value, page: 1 })} style={{ paddingLeft: 36 }} />
          </div>
          <button className={`btn btn-sm ${showFilters ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setShowFilters(!showFilters)}>
            <Filter size={14} /> Filtros{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={exportCSV}><Download size={14} /> CSV</button>
          <button className="btn btn-primary btn-sm" onClick={() => setShowManual(true)}><Plus size={14} /> Agregar lead</button>
        </div>

        {showFilters && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14, padding: 14, background: 'var(--bg-secondary)', borderRadius: 10, border: '1px solid var(--border)' }}>
            <select value={filters.status || ''} onChange={e => setFilters({ ...filters, status: e.target.value, page: 1 })} style={{ width: 170 }}>
              <option value="">Todos los estados</option>
              {/* "Diagnóstico enviado" solo aparece si en lo que se está viendo hay prospectos */}
              {(filterOptions.sources?.includes('prospeccion') ? Object.entries(STATUS_CONFIG) : estadosPara(null))
                .map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select value={filters.qualification || ''} onChange={e => setFilters({ ...filters, qualification: e.target.value, page: 1 })} style={{ width: 170 }}>
              <option value="">Todas las calificaciones</option>
              {Object.entries(QUALIFICATION_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
            </select>
            <select value={filters.city || ''} onChange={e => setFilters({ ...filters, city: e.target.value, page: 1 })} style={{ width: 160 }}>
              <option value="">Todas las ciudades</option>
              {filterOptions.cities?.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={filters.category || ''} onChange={e => setFilters({ ...filters, category: e.target.value, page: 1 })} style={{ width: 160 }}>
              <option value="">Todos los sectores</option>
              {filterOptions.categories?.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={filters.source || ''} onChange={e => setFilters({ ...filters, source: e.target.value, page: 1 })} style={{ width: 150 }}>
              <option value="">Todas las fuentes</option>
              {filterOptions.sources?.map(s => <option key={s} value={s}>{SOURCE_CONFIG[s]?.label || s}</option>)}
            </select>
            {filterOptions.utmCampaigns?.length > 0 && (
              <select value={filters.utm_campaign || ''} onChange={e => setFilters({ ...filters, utm_campaign: e.target.value, page: 1 })} style={{ width: 170 }}>
                <option value="">Todas las campañas</option>
                {filterOptions.utmCampaigns.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            <button className="btn btn-ghost btn-sm" onClick={() => setFilters({ ...filters, status: '', qualification: '', city: '', category: '', source: '', utm_campaign: '', page: 1 })}>Limpiar</button>
          </div>
        )}
        {(hayProspectos || filters.creado) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 10, fontSize: 13 }}>
            <span style={{ color: 'var(--text-muted)' }}>Cargados:</span>
            {[['', 'Todos'], [hoyAr(), 'Hoy'], [diaAntes(hoyAr()), 'Ayer']].map(([v, l]) => (
              <button key={l} className={'btn btn-sm ' + ((filters.creado || '') === v ? 'btn-primary' : 'btn-secondary')}
                onClick={() => setFilters({ ...filters, creado: v, page: 1 })}>{l}</button>
            ))}
            <input type="date" value={filters.creado || ''} onChange={e => setFilters({ ...filters, creado: e.target.value, page: 1 })}
              style={{ width: 150, padding: '4px 8px', fontSize: 12 }} />
          </div>
        )}
        {/* Rubros: se eligen uno o varios, para ver solo los corralones o solo los consultorios */}
        {(hayProspectos || filters.rubros) && filterOptions.categories?.length > 0 && (() => {
          const elegidos = String(filters.rubros || '').split('|').filter(Boolean);
          const alternar = (r) => {
            const n = elegidos.includes(r) ? elegidos.filter(x => x !== r) : [...elegidos, r];
            setFilters({ ...filters, rubros: n.join('|'), page: 1 });
          };
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 10, fontSize: 13 }}>
              <span style={{ color: 'var(--text-muted)' }}>Rubro:</span>
              <button className={'btn btn-sm ' + (elegidos.length ? 'btn-secondary' : 'btn-primary')}
                onClick={() => setFilters({ ...filters, rubros: '', page: 1 })}>Todos</button>
              {filterOptions.categories.map(r => (
                <button key={r} className={'btn btn-sm ' + (elegidos.includes(r) ? 'btn-primary' : 'btn-secondary')}
                  onClick={() => alternar(r)}>{r}</button>
              ))}
            </div>
          );
        })()}
        {/* El experimento de prospección: por línea de WhatsApp y por si habla con el dueño */}
        {(hayProspectos || filters.linea || filters.decisor) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 10, fontSize: 13 }}>
            <span style={{ color: 'var(--text-muted)' }}>Línea:</span>
            {[['', 'Todas'], ...Object.entries(LINEAS).map(([k, v]) => [k, v.label]), ['sin', 'Sin dato']].map(([v, l]) => (
              <button key={'l' + v} className={'btn btn-sm ' + ((filters.linea || '') === v ? 'btn-primary' : 'btn-secondary')}
                onClick={() => setFilters({ ...filters, linea: v, page: 1 })}>{l}</button>
            ))}
            <span style={{ color: 'var(--text-muted)', marginLeft: 12 }}>Habla con el dueño:</span>
            {[['', 'Todos'], ...Object.entries(DECISOR), ['sin', 'Sin dato']].map(([v, l]) => (
              <button key={'d' + v} className={'btn btn-sm ' + ((filters.decisor || '') === v ? 'btn-primary' : 'btn-secondary')}
                onClick={() => setFilters({ ...filters, decisor: v, page: 1 })}>{l}</button>
            ))}
          </div>
        )}
        {/* Prospección por mail: por tipo de negocio y por cómo respondió, y la casilla a mano */}
        {(hayMail || filters.tipo || filters.respuesta) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 10, fontSize: 13 }}>
            <span style={{ color: 'var(--text-muted)' }}>Tipo:</span>
            {[['', 'Todos'], ...Object.entries(TIPOS_MAIL)].map(([v, l]) => (
              <button key={'t' + v} className={'btn btn-sm ' + ((filters.tipo || '') === v ? 'btn-primary' : 'btn-secondary')}
                onClick={() => setFilters({ ...filters, tipo: v, page: 1 })}>{l}</button>
            ))}
            <span style={{ color: 'var(--text-muted)', marginLeft: 12 }}>Respuesta:</span>
            {[['', 'Todas'], ...Object.entries(RESPUESTAS_MAIL).map(([k, v]) => [k, v.label])].map(([v, l]) => (
              <button key={'r' + v} className={'btn btn-sm ' + ((filters.respuesta || '') === v ? 'btn-primary' : 'btn-secondary')}
                onClick={() => setFilters({ ...filters, respuesta: v, page: 1 })}>{l}</button>
            ))}
            {webmail && (
              <a className="btn btn-sm btn-secondary" href={webmail} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 'auto' }}
                title="El webmail de la casilla que manda los mails de prospección"><Mail size={13} /> Abrir casilla <ExternalLink size={11} /></a>
            )}
          </div>
        )}
        {typeof total === 'number' && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>{total} leads encontrados</div>}
      </>)}

      <div className="table-container">
        <table>
          <thead><tr>
            <th style={{width:30}}></th>
            <th>Negocio</th>
            {hayProspectos && <th>Rubro</th>}
            <th>Teléfono</th>
            {showDatasetCol && <th>Base</th>}
            <th>Origen</th>
            <th>Estado</th>
            {/* Con prospectos, la calificación se ve y se edita en el panel del lead: en la lista va el rubro */}
            {!hayProspectos && <th>Calificación</th>}
            {customCols.map(c => <th key={c.key}>{c.label}</th>)}
            {hayProspectos && <th>Observación</th>}
            <th>Ingreso</th>
            <th>Último contacto</th>
            <th style={{width:74}}></th>
          </tr></thead>
          <tbody>
            {leads.length === 0 ? (
              <tr><td colSpan={10 + (showDatasetCol?1:0) + customCols.length + (hayProspectos?1:0)} style={{ textAlign:'center', padding:40, color:'var(--text-muted)' }}>No se encontraron leads</td></tr>
            ) : leads.map(lead => {
              const sc = estadoInfo(lead.status, lead.source);
              const qc = QUALIFICATION_CONFIG[lead.qualification] || {};
              return (
                <tr key={lead.id} onClick={() => openLead(lead)} style={{ cursor:'pointer', background: selectedLead?.id === lead.id ? 'var(--accent-glow)' : (esAgencia ? tenue(sc.bg) : 'transparent') }}>
                  <td onClick={e => { e.stopPropagation(); api.put('/leads/'+lead.id+'/favorite').then(onRefresh); }}>
                    <Star size={14} fill={lead.is_favorite ? '#eab308' : 'none'} color={lead.is_favorite ? '#eab308' : 'var(--text-muted)'} style={{cursor:'pointer'}}/>
                  </td>
                  <td style={{ fontWeight:600, maxWidth:220, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {lead.business_name || '—'}
                    {lead.source === 'prospeccion' && LINEAS[valorDe(lead, 'linea')] && (
                      <span className="badge" title="Línea de WhatsApp desde la que se le escribió"
                        style={{ fontSize:10, marginLeft:6, background: LINEAS[valorDe(lead, 'linea')].color + '22', color: LINEAS[valorDe(lead, 'linea')].color }}>
                        {LINEAS[valorDe(lead, 'linea')].label}</span>
                    )}
                    {lead.source === 'prospeccion_mail' && (
                      <span className="badge" title={`Mails de la secuencia enviados${valorDe(lead, 'proximo_envio') ? ' · próximo: ' + valorDe(lead, 'proximo_envio') : ''}`}
                        style={{ fontSize:10, marginLeft:6, background:'rgba(8,145,178,0.15)', color:'#0891b2' }}>
                        <Mail size={10} style={{ verticalAlign:-1 }} /> {Number(valorDe(lead, 'paso') || 0)}/3</span>
                    )}
                    {lead.source === 'prospeccion_mail' && RESPUESTAS_MAIL[valorDe(lead, 'respuesta')] && valorDe(lead, 'respuesta') !== 'ninguna' && (
                      <span className="badge" style={{ fontSize:10, marginLeft:4, background: RESPUESTAS_MAIL[valorDe(lead, 'respuesta')].color + '22', color: RESPUESTAS_MAIL[valorDe(lead, 'respuesta')].color }}>
                        {RESPUESTAS_MAIL[valorDe(lead, 'respuesta')].label}</span>
                    )}
                    {lead.source === 'prospeccion' && DECISOR[valorDe(lead, 'decisor')] && (
                      <span className="badge" title="¿Habla con el dueño o con quien decide?"
                        style={{ fontSize:10, marginLeft:4, background:'rgba(100,116,139,0.15)', color:'var(--text-secondary)' }}>
                        Dueño: {DECISOR[valorDe(lead, 'decisor')]}</span>
                    )}
                  </td>
                  {hayProspectos && <td style={{ fontSize:12, color:'var(--text-secondary)', whiteSpace:'nowrap' }}>{lead.category || ''}</td>}
                  <td style={{ fontSize:13 }}>{esAgencia
                    ? (lead.phone ? <span title={lead.phone} style={{ color:'#22c55e', fontWeight:700 }}>✓</span> : <span style={{ color:'var(--text-muted)' }}>—</span>)
                    : (lead.phone || '—')}</td>
                  {showDatasetCol && <td style={{ fontSize:12, color:'var(--text-muted)' }}>{lead.dataset_name || '—'}</td>}
                  <td><span className="badge" style={{ fontSize:10, background:'rgba(46,125,226,0.12)', color:'var(--accent)' }}>{getSourceLabel(lead)}</span></td>
                  <td><select value={lead.status} onChange={async e => { e.stopPropagation(); try { await api.put('/leads/'+lead.id+'/status', { status: e.target.value }); onRefresh(); } catch {} }} onClick={e => e.stopPropagation()} style={{ background: sc.bg || 'rgba(100,116,139,0.15)', color: sc.color || '#64748b', border:'none', borderRadius:20, padding:'4px 8px', fontSize:11, fontWeight:600, cursor:'pointer' }}>
                    {estadosPara(lead.source, lead.status).map(([k,v]) => <option key={k} value={k} style={{ background:'var(--bg-card)', color:'var(--text-primary)' }}>{v.label}</option>)}
                  </select></td>
                  {!hayProspectos && <td><select value={lead.qualification} onChange={async e => { e.stopPropagation(); try { await api.put('/leads/'+lead.id+'/qualification', { qualification: e.target.value }); onRefresh(); } catch {} }} onClick={e => e.stopPropagation()} style={{ background: qc.bg || 'rgba(100,116,139,0.15)', color: qc.color || '#64748b', border:'none', borderRadius:20, padding:'4px 8px', fontSize:11, fontWeight:600, cursor:'pointer' }}>
                    {Object.entries(QUALIFICATION_CONFIG).map(([k,v]) => <option key={k} value={k} style={{ background:'var(--bg-card)', color:'var(--text-primary)' }}>{v.label}</option>)}
                  </select></td>}
                  {customCols.map(c => <td key={c.key} style={{ fontSize:12, color:'var(--text-secondary)', whiteSpace:'nowrap' }}>{getExtraField(lead, c)}</td>)}
                  {hayProspectos && <td title={String(valorDe(lead, 'observacion') || '')} style={{ fontSize:12, color:'var(--text-secondary)', maxWidth:220, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{valorDe(lead, 'observacion') || '—'}</td>}
                  <td style={{ fontSize:12, color:'var(--text-muted)' }}>{formatDateShort(lead.created_at)}</td>
                  <td><input type="date" value={lead.last_contacted_at ? lead.last_contacted_at.split('T')[0] : ''} onChange={async e => { e.stopPropagation(); try { await api.put('/leads/'+lead.id+'/last-contact', { date: e.target.value }); onRefresh(); } catch {} }} onClick={e => e.stopPropagation()} style={{ background:'transparent', border:'1px solid var(--border)', borderRadius:6, padding:'2px 6px', fontSize:11, color:'var(--text-secondary)', width:120, cursor:'pointer' }} /></td>
                  <td><div style={{ display:'flex', gap:2, justifyContent:'flex-end' }}>
                    {esProspecto(lead.source) && lead.website && (
                      <a className="btn btn-ghost btn-sm" title="Abrir su web" onClick={e => e.stopPropagation()}
                        href={/^https?:\/\//i.test(lead.website) ? lead.website : 'https://' + lead.website}
                        target="_blank" rel="noopener noreferrer" style={{ color:'var(--accent)', padding:4 }}><Globe size={14}/></a>
                    )}
                    {esProspecto(lead.source) && /^https?:\/\//i.test(String(valorDe(lead, 'maps') || '')) && (
                      <a className="btn btn-ghost btn-sm" title="Abrir en Google Maps" onClick={e => e.stopPropagation()}
                        href={valorDe(lead, 'maps')} target="_blank" rel="noopener noreferrer" style={{ color:'#ef4444', padding:4 }}><MapPin size={14}/></a>
                    )}
                    {canWhatsApp && lead.phone && <button className="btn btn-ghost btn-sm" title="Contactar por WhatsApp" onClick={e => { e.stopPropagation(); setWaLead(lead); }} style={{ color:'#22c55e', padding:4 }}><MessageCircle size={14}/></button>}
                    <button className="btn btn-ghost btn-sm" title="Eliminar" onClick={e => { e.stopPropagation(); if(window.confirm('¿Eliminar?')) api.delete('/leads/'+lead.id).then(onRefresh); }} style={{ color:'var(--danger)', padding:4 }}><Trash2 size={14}/></button>
                  </div></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div style={{ display:'flex', justifyContent:'center', gap:8, marginTop:16 }}>
          <button className="btn btn-secondary btn-sm" disabled={page<=1} onClick={() => setFilters({...filters, page: page-1})}>Anterior</button>
          <span style={{ color:'var(--text-muted)', fontSize:13, padding:'6px 12px' }}>Página {page} de {pages} ({total} leads)</span>
          <button className="btn btn-secondary btn-sm" disabled={page>=pages} onClick={() => setFilters({...filters, page: page+1})}>Siguiente</button>
        </div>
      )}

      {selectedLead && (
        <LeadDetail leadId={selectedLead.id} onClose={() => setSelectedLead(null)} onRefresh={() => { onRefresh(); }} />
      )}

      {showManual && <ManualLeadModal datasets={datasets} defaultDatasetId={datasetId} onClose={() => { setShowManual(false); onRefresh(); }} />}

      {waLead && <WhatsAppModal lead={waLead} onClose={() => { setWaLead(null); onRefresh(); }} />}
    </>
  );
}
